import { cn } from '@/lib/utils'
import type { MiniSparklineContract, OperationalSeries } from './types'

export interface MiniSparklineProps {
  series?: OperationalSeries
  expected: MiniSparklineContract
  ariaLabel?: string
  className?: string
}

const bucketMsByGranularity: Record<MiniSparklineContract['granularity'], number> = {
  '4h': 4 * 60 * 60 * 1000,
  day: 24 * 60 * 60 * 1000,
}

function parseIso(value: string) {
  const timestamp = Date.parse(value)
  return Number.isNaN(timestamp) ? null : timestamp
}

export function validateMiniSparklineSeries(
  series: OperationalSeries | undefined,
  expected: MiniSparklineContract,
) {
  if (!series) return false
  if (series.window !== expected.window) return false
  if (series.granularity !== expected.granularity) return false
  if (!Array.isArray(series.points)) return false
  if (series.points.length !== expected.pointCount) return false

  const expectedBucketMs = bucketMsByGranularity[expected.granularity]
  let previousTo: number | null = null

  for (const point of series.points) {
    const from = parseIso(point.from)
    const to = parseIso(point.to)
    if (from === null || to === null) return false
    if (to <= from) return false
    if (to - from !== expectedBucketMs) return false
    if (previousTo !== null && from !== previousTo) return false
    if (!Number.isFinite(point.count) || point.count < 0) return false
    if (Math.floor(point.count) !== point.count) return false
    previousTo = to
  }

  return true
}

function buildPolyline(points: OperationalSeries['points']) {
  const width = 240
  const height = 72
  const paddingX = 8
  const paddingY = 8
  const max = Math.max(...points.map((point) => point.count), 0)
  const drawableWidth = width - paddingX * 2
  const drawableHeight = height - paddingY * 2

  return points
    .map((point, index) => {
      const x =
        points.length === 1
          ? width / 2
          : paddingX + (drawableWidth * index) / (points.length - 1)
      const normalized = max === 0 ? 0 : point.count / max
      const y = height - paddingY - normalized * drawableHeight
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
}

export function MiniSparkline({
  series,
  expected,
  ariaLabel = 'Operational trend',
  className,
}: MiniSparklineProps) {
  const isValid = validateMiniSparklineSeries(series, expected)

  if (!isValid || !series) {
    return (
      <div
        className={cn(
          'flex min-h-[112px] items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 p-3 text-center text-sm text-muted-foreground',
          className,
        )}
        role="status"
      >
        Sparkline unavailable: expected {expected.pointCount} contiguous{' '}
        {expected.granularity} buckets for {expected.window}.
      </div>
    )
  }

  const validSeries = series
  const allZero = validSeries.points.every((point) => point.count === 0)
  const polyline = buildPolyline(validSeries.points)

  return (
    <figure
      className={cn('min-h-[112px] rounded-lg border border-border p-3', className)}
      role="group"
      aria-label={ariaLabel}
    >
      <svg
        viewBox="0 0 240 72"
        className="h-20 w-full overflow-visible"
        aria-hidden="true"
        focusable="false"
      >
        <line
          x1="8"
          y1="64"
          x2="232"
          y2="64"
          stroke="currentColor"
          strokeWidth="1"
          className="text-muted-foreground/40"
        />
        <polyline
          points={polyline}
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="3"
          vectorEffect="non-scaling-stroke"
          className={allZero ? 'text-muted-foreground' : 'text-primary'}
        />
      </svg>
      <p className="text-xs font-medium text-muted-foreground">
        {allZero ? 'Flat zero line: no events in this window.' : 'Trend line'}
      </p>
      <ol className="sr-only" aria-label="Sparkline buckets">
        {validSeries.points.map((point) => (
          <li key={`${point.from}-${point.to}`}>
            {point.label}: from {point.from} to {point.to}, count {point.count}
          </li>
        ))}
      </ol>
    </figure>
  )
}
